import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * ACT composite, STEM and superscore.
 *
 * Source: ACT, "Interpreting Scores on the Enhanced ACT: Guidance for K-12 and Higher
 * Education Institutions" (2025), and ACT, "Understanding Your ACT Scores" (act.org,
 * checked 2026):
 *
 * - Enhanced ACT: Composite = rounded average of English, math and reading ("EMR").
 *   Science is optional and reported separately, with a STEM score (the average of math
 *   and science). Online National tests from April 2025 and all tests from September
 *   2025 report the EMR composite.
 * - Legacy ACT (earlier tests): Composite = rounded average of English, math, reading
 *   and science ("EMRS").
 * - Rounding: "Fractions less than one-half are rounded down; fractions one-half or more
 *   are rounded up."
 * - Superscore: the best English, math and reading scores from any tests since
 *   September 2016, legacy or enhanced, averaged and rounded the same way. Science no
 *   longer counts in it; the report still shows the highest science and STEM scores.
 */

export type ActVersion = "enhanced" | "legacy";

export interface ActTest {
  version: ActVersion;
  english: number;
  math: number;
  reading: number;
  /** Optional on the enhanced ACT. */
  science: number | null;
}

/** ACT's rule: halves round up. */
export function actRound(value: number): number {
  return Math.floor(cleanFloat(value) + 0.5);
}

export function isActScore(value: number | null): value is number {
  return value !== null && Number.isInteger(value) && value >= 1 && value <= 36;
}

/** Null when a legacy test has no science score to average. */
export function actComposite(test: ActTest): number | null {
  if (test.version === "legacy") {
    if (test.science === null) {
      return null;
    }
    return actRound(
      (test.english + test.math + test.reading + test.science) / 4
    );
  }
  return actRound((test.english + test.math + test.reading) / 3);
}

export function actStem(test: ActTest): number | null {
  if (test.science === null) {
    return null;
  }
  return actRound((test.math + test.science) / 2);
}

export interface ActBest {
  score: number;
  /** Index of the first test that earned it. */
  test: number;
}

function bestOf(
  tests: readonly ActTest[],
  pick: (test: ActTest) => number | null
): ActBest | null {
  let best: ActBest | null = null;
  for (const [index, test] of tests.entries()) {
    const score = pick(test);
    if (score !== null && (best === null || score > best.score)) {
      best = { score, test: index };
    }
  }
  return best;
}

export interface ActSuperscore {
  english: ActBest;
  math: ActBest;
  reading: ActBest;
  science: ActBest | null;
  /** ACT's current superscore: best English, math and reading. */
  composite: number;
  /** The pre-September-2025 four-section version, when any science score exists. */
  legacyComposite: number | null;
}

export function actSuperscore(tests: readonly ActTest[]): ActSuperscore | null {
  const english = bestOf(tests, (test) => test.english);
  const math = bestOf(tests, (test) => test.math);
  const reading = bestOf(tests, (test) => test.reading);
  if (english === null || math === null || reading === null) {
    return null;
  }
  const science = bestOf(tests, (test) => test.science);
  return {
    english,
    math,
    reading,
    science,
    composite: actRound((english.score + math.score + reading.score) / 3),
    legacyComposite:
      science === null
        ? null
        : actRound(
            (english.score + math.score + reading.score + science.score) / 4
          ),
  };
}
